// TEMPORARY diagnostic layer for the Android test APK ONLY.
// Enabled exclusively when the bundle is built with REACT_APP_DIAG_PANEL=true
// (never set for web/production deploys). Everything no-ops when disabled.
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';

import { getAppInfo } from '../version';

export const DIAG_ENABLED = process.env.REACT_APP_DIAG_PANEL === 'true';
export const BUILD_HASH = process.env.REACT_APP_BUILD_HASH || 'nohash';
let VERSION_CODE = '?';
let VERSION_NAME = '?';
export let BUILD_ID = `DIAG?-${BUILD_HASH}`;
if (DIAG_ENABLED) {
  getAppInfo().then((i) => {
    VERSION_CODE = i.build || 'web';
    VERSION_NAME = i.version || 'web';
    BUILD_ID = `DIAG${VERSION_CODE}-${BUILD_HASH}`;
  }).catch(() => {});
}
const KEY = 'wassla_diag_log';
const NATIVE_FILE = 'wassla_native_diag.txt';
const MAX = 300;

let buf = [];

export function diag(tag, data) {
  if (!DIAG_ENABLED) return;
  try {
    buf.push({
      t: new Date().toISOString().slice(11, 23),
      e: tag,
      d: data ? JSON.stringify(data).slice(0, 500) : '',
      r: (window.location.hash || window.location.pathname || '').slice(0, 80),
      v: document.visibilityState,
    });
    if (buf.length > MAX) buf = buf.slice(-MAX);
    localStorage.setItem(KEY, JSON.stringify(buf));
    console.log('[DIAG]', tag, data || '');
  } catch (_e) { /* diagnostics must never break the app */ }
}

function fmt(entry) {
  return `${entry.t} [${entry.v}] ${entry.e} ${entry.d || ''} @${entry.r}`;
}

async function readNativeLog() {
  if (!Capacitor.isNativePlatform()) return '(web: no native log)';
  try {
    const res = await Filesystem.readFile({ path: NATIVE_FILE, directory: Directory.Data, encoding: Encoding.UTF8 });
    const lines = String(res.data).trim().split('\n');
    return lines.slice(-80).join('\n');
  } catch (e) {
    return '(native log not readable: ' + (e?.message || e) + ')';
  }
}

function panelHeader() {
  const nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
  const scripts = Array.from(document.scripts).map((s) => (s.src || '').split('/').pop()).filter(Boolean).join(', ');
  const rootEl = document.getElementById('root');
  const host = window.location.hostname;
  const proto = window.location.protocol;
  const bundledLocal = (host === 'localhost' || host === '') && (proto === 'https:' || proto === 'capacitor:' || proto === 'file:');
  return [
    `BUILD: ${BUILD_ID}  |  versionCode: ${VERSION_CODE}  |  versionName: ${VERSION_NAME}`,
    `git/build hash: ${BUILD_HASH}`,
    `executing bundle: ${scripts}`,
    `webview url: ${window.location.href.slice(0, 120)}`,
    `route: ${window.location.hash || window.location.pathname}`,
    `asset source: ${bundledLocal ? 'BUNDLED LOCAL ASSETS (capacitor localhost)' : 'REMOTE/OTHER: ' + proto + '//' + host}`,
    `boot type: ${nav.type || '?'}  |  visibility: ${document.visibilityState}`,
    `react root mounted: ${rootEl ? rootEl.childElementCount > 0 : 'no #root'}`,
    `cv preview in DOM: ${!!document.getElementById('cv-print-root')}`,
  ].join('\n');
}

function buildPanel() {
  const chip = document.createElement('div');
  chip.textContent = `DIAG ${VERSION_CODE} · ${BUILD_HASH}`;
  chip.setAttribute('data-testid', 'diag-chip');
  chip.style.cssText = 'position:fixed;bottom:96px;left:6px;z-index:2147483647;background:#111;color:#0f0;font:bold 11px monospace;padding:5px 8px;border-radius:6px;opacity:0.75;';
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2147483647;background:#0b0b0b;color:#ddd;font:11px/1.5 monospace;display:none;flex-direction:column;padding:8px;';
  const bar = document.createElement('div');
  bar.style.cssText = 'display:flex;gap:6px;margin-bottom:6px;flex-wrap:wrap;';
  const pre = document.createElement('pre');
  pre.style.cssText = 'flex:1;overflow:auto;white-space:pre-wrap;word-break:break-all;margin:0;background:#000;padding:6px;border-radius:6px;';
  const mkBtn = (label, fn) => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = 'background:#222;color:#fff;border:1px solid #555;border-radius:5px;padding:6px 10px;font:12px monospace;';
    b.onclick = fn;
    return b;
  };
  let fullText = '';
  const render = async () => {
    const nativeLog = await readNativeLog();
    const jsLog = buf.slice().reverse().map(fmt).join('\n');
    fullText = `=== HEADER ===\n${panelHeader()}\n\n=== NATIVE LIFECYCLE (oldest→newest) ===\n${nativeLog}\n\n=== JS EVENTS (newest first) ===\n${jsLog}`;
    pre.textContent = fullText;
  };
  bar.appendChild(mkBtn('Refresh', render));
  bar.appendChild(mkBtn('Copy', () => {
    try { navigator.clipboard.writeText(fullText); } catch (_e) { /* noop */ }
  }));
  bar.appendChild(mkBtn('Clear', async () => {
    buf = [];
    try { localStorage.removeItem(KEY); } catch (_e) { /* noop */ }
    try { await Filesystem.deleteFile({ path: NATIVE_FILE, directory: Directory.Data }); } catch (_e) { /* noop */ }
    render();
  }));
  bar.appendChild(mkBtn('Close', () => { overlay.style.display = 'none'; }));
  overlay.appendChild(bar);
  overlay.appendChild(pre);
  chip.onclick = () => { overlay.style.display = 'flex'; render(); };
  document.body.appendChild(chip);
  document.body.appendChild(overlay);
}

export function initDiagnostics() {
  if (!DIAG_ENABLED) return;
  try { buf = JSON.parse(localStorage.getItem(KEY)) || []; } catch (_e) { buf = []; }
  const nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
  diag('JS_BOOT', {
    build: BUILD_ID,
    bootType: nav.type,
    scripts: Array.from(document.scripts).map((s) => (s.src || '').split('/').pop()).filter(Boolean),
  });
  window.addEventListener('error', (e) => diag('JS_ERROR', { msg: String(e.message).slice(0, 200), src: `${e.filename}:${e.lineno}` }));
  window.addEventListener('unhandledrejection', (e) => diag('JS_UNHANDLED_REJECTION', { msg: String(e.reason?.message || e.reason).slice(0, 200) }));
  window.addEventListener('wasslaNative', (e) => diag('NATIVE_EVT', e.detail));
  document.addEventListener('visibilitychange', () => diag('VISIBILITY', { s: document.visibilityState }));
  window.addEventListener('pageshow', (e) => diag('PAGESHOW', { persisted: e.persisted }));
  window.addEventListener('pagehide', () => diag('PAGEHIDE'));
  if (document.body) buildPanel();
  else document.addEventListener('DOMContentLoaded', buildPanel);
}
